import React, { useRef, useState } from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  Input,
  InputGroup,
  InputLeftElement,
  List,
  ListItem,
  Flex,
  Center,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Button,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Spinner,
  Icon,
  Tooltip,
  Radio,
  RadioGroup,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverHeader,
  PopoverBody,
  PopoverArrow,
  PopoverCloseButton,
} from "@chakra-ui/react";
import { 
  SearchIcon, 
  ChevronDownIcon, 
  DeleteIcon, 
  CopyIcon, 
  AddIcon,
  EditIcon
} from "@chakra-ui/icons";
import { Header } from "build-ts/metadata";
import { deleteCustomAgent, cloneCustomAgent, listCustomAgents, updateAgentHeader } from "services/CustomAgentServices";
import { CustomAgentProto } from "build-ts/custom_agent";
import showToast from "hooks/useCustomToast";
import { FaFilter } from "react-icons/fa6";

interface AgentListComponentProps {
  filteredHeaders: Header[];
  selectedAgents: Header[];
  searchTerm: string;
  onSearchChange: (term: string) => void;
  onAgentSelection?: (header: Header, preloadedAgentData?: CustomAgentProto | null) => void;
  onCreateNewAgent?: () => Promise<void>;
  setAgentHeaders?: (headers: Header[]) => void;
  isLoading?: boolean;
}

const AgentListComponent: React.FC<AgentListComponentProps> = ({
  filteredHeaders,
  selectedAgents,
  searchTerm,
  onSearchChange,
  onAgentSelection,
  onCreateNewAgent,
  setAgentHeaders,
  isLoading = false,
}) => {
  const [editingAgent, setEditingAgent] = useState<Header | null>(null);
  const [newAgentName, setNewAgentName] = useState("");
  const [deletingAgent, setDeletingAgent] = useState<Header | null>(null);
  const [duplicatingAgent, setDuplicatingAgent] = useState<Header | null>(null);
  // Filter state
  const [filterType, setFilterType] = useState<"all" | "active" | "inactive">("all");
  const [isFilterPopoverOpen, setIsFilterPopoverOpen] = useState(false);
  
  const { 
    isOpen: isRenameOpen,
    onOpen: onRenameOpen,
    onClose: onRenameClose,
  } = useDisclosure();
  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure();

  const cancelRef = useRef<HTMLButtonElement>(null);

  const handleAgentSelection = (header: Header) => {
    if (onAgentSelection) {
      onAgentSelection(header);
    }
  };

  const handleRenameClick = (header: Header) => {
    setEditingAgent(header);
    setNewAgentName(header.name || "");
    onRenameOpen();
  };

  const handleDeleteClick = (header: Header) => {
    setDeletingAgent(header);
    onDeleteOpen();
  };

  const handleDuplicateClick = async (header: Header) => {
    if (!header?.id) return;
    
    setDuplicatingAgent(header);
    // Use the clone service to clone the agent
    const clonedAgent = await cloneCustomAgent(header.id, true); // Clone and save
    
    if (clonedAgent && clonedAgent.header) {
      // Add the cloned agent to the list
      const updatedHeaders = [clonedAgent.header, ...filteredHeaders];
      setAgentHeaders?.(updatedHeaders);
      
      // Select the cloned agent with preloaded data to avoid redundant API call
      if (onAgentSelection) {
        onAgentSelection(clonedAgent.header, clonedAgent);
      }
    }
    setDuplicatingAgent(null);
  };

  const handleCreateNewAgent = () => {
    if (onCreateNewAgent) {
      onCreateNewAgent();
    }
  };

  const handleConfirmRename = async () => {
    if (!editingAgent || !newAgentName.trim()) return;
    
    const response = await updateAgentHeader(editingAgent?.id!, newAgentName.trim(), editingAgent?.description);
    if (response) {
      // Update the local state
      const updatedHeaders = filteredHeaders.map(header => 
        header?.id === editingAgent?.id 
          ? { ...header, name: newAgentName.trim() }
          : header
      );
      setAgentHeaders?.(updatedHeaders);
      // Update selected agent if it's the one being renamed
      if (selectedAgents.some(h => h.id === editingAgent?.id)) {
        const updatedAgent = { ...editingAgent, name: newAgentName.trim() };
        // Call onAgentSelection to refresh the agent details in the form
        onAgentSelection?.(updatedAgent);
      }
    }
    onRenameClose();
    setEditingAgent(null);
    setNewAgentName("");
  };

  const handleConfirmDelete = async () => {
    if (!deletingAgent?.id) return;
    
    try {
      await deleteCustomAgent(deletingAgent?.id);
      
      // Remove the deleted agent from the list
      const updatedHeaders = filteredHeaders.filter(h => h.id !== deletingAgent.id);
      setAgentHeaders?.(updatedHeaders);
      
      showToast("Agent deleted successfully", "", "success");
      onDeleteClose();
    } catch (error) {
      showToast("Failed to delete agent", "", "error");
    }
  };

  const fetchFilteredAgents = async (filterType: "all" | "active" | "inactive") => {
    try {
      let headers: {headers: Header[], totalRows: number} | null = null;
      
      if (filterType === "all") {
        // Fetch all agents without filtering
        headers = await listCustomAgents(
          -1, // all jobs
          1, // page 1
          "LAST_MODIFIED", // sort by last modified
          true, // sort ascending
          true, // includeScheduleActivity
          "" // activeSchedule
        );
      } else if (filterType === "active") {
        // Fetch active agents
        headers = await listCustomAgents(
          -1, // all jobs
          1, // page 1
          "LAST_MODIFIED", // sort by last modified
          true, // sort ascending
          true, // includeScheduleActivity
          true // activeSchedule
        );
      } else if (filterType === "inactive") {
        // Fetch inactive agents
        headers = await listCustomAgents(
          -1, // all jobs
          1, // page 1
          "LAST_MODIFIED", // sort by last modified
          true, // sort ascending
          true, // includeScheduleActivity
          false // activeSchedule
        );
      }
      
      if (headers && setAgentHeaders) {
        setAgentHeaders(headers?.headers || []);
      }
    } catch (error) {
      console.error("Error fetching filtered agents:", error);
    }
  };

  const handleFilterChange = (newFilterType: "all" | "active" | "inactive") => {
    setFilterType(newFilterType);
    setIsFilterPopoverOpen(false);
    fetchFilteredAgents(newFilterType);
  };

  return (
    <>
      <Box
        height="100%"
        bg="gray.50"
        borderRight="1px"
        borderColor="gray.200"
        borderRadius="lg"
      >
        <VStack spacing={2} p={2} align="stretch" height="100%" overflowY="auto">
          {/* Search and Create New Agent Button */}
          <HStack spacing={2}>
            <InputGroup flex="1">
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="Search agents..."
                value={searchTerm}
                onChange={(e) => onSearchChange(e.target.value)}
                bg="white"
                size="sm"
                _focus={{
                  borderColor: "purple.700",
                  boxShadow: "0 0 0 1px var(--chakra-colors-purple-700)"
                }}
              />
            </InputGroup>
            <Button
              leftIcon={<AddIcon boxSize={3}/>}
              onClick={handleCreateNewAgent}
              size="xs"
              aria-label="Create New Agent"
              borderRadius="md"
              title="Create New Agent"
              bg="gray.50"
              color="purple.700"
              _hover={{ bg: "purple.700", color: "white" }}
              variant="outline"
              borderWidth="2px"
              borderColor="purple.700"
              fontWeight="700"
              iconSpacing={1}
              cursor="pointer"
            >
              New
            </Button>
          </HStack>

          {/* AGENTS Heading with Filter */}
          <HStack justify="space-between" align="center" mx={1} p={0}>
            <Text 
              fontSize="xs" 
              fontWeight="bold" 
              color="gray.500" 
              letterSpacing="wider"
              textTransform="uppercase"
              mb={0}
            >
              Agents
            </Text>
            {/* Divider Line */}
            <Box 
              flex="1" 
              height="1px" 
              bg="gray.300" 
              mx={0}
            />
            <Popover 
              isOpen={isFilterPopoverOpen} 
              onClose={() => setIsFilterPopoverOpen(false)}
              placement="bottom-end"
            >
              <PopoverTrigger>
                <IconButton
                  aria-label="Filter agents"
                  icon={<Icon as={FaFilter} />}
                  onClick={() => setIsFilterPopoverOpen(!isFilterPopoverOpen)}
                  size="xs"
                  variant="ghost"
                  color={filterType !== "all" ? "purple.600" : "gray.500"}
                  bg={filterType !== "all" ? "purple.50" : "transparent"}
                  _hover={{ color: "purple.600", bg: filterType !== "all" ? "purple.100" : "gray.50" }}
                  cursor="pointer"
                />
              </PopoverTrigger>
              <PopoverContent w="180px">
                <PopoverArrow />
                <PopoverHeader fontWeight="bold" fontSize="sm" m={1}>
                  <Text align="left" m={0}>Filter Agents</Text>
                </PopoverHeader>
                <PopoverCloseButton size="xs" m={1} />
                <PopoverBody>
                  <RadioGroup value={filterType} onChange={handleFilterChange}>
                    <VStack align="start">
                      <HStack spacing={2}>
                        <Radio value="all" size="sm" m={0}/>
                        <Text fontSize="sm" m={0}>All Agents</Text>
                      </HStack>
                      <HStack spacing={2}>
                        <Radio value="active" size="sm" m={0}/>
                        <Text fontSize="sm" m={0}>Active Agents</Text>
                      </HStack>
                      <HStack spacing={2}>
                        <Radio value="inactive" size="sm" m={0}/>
                        <Text fontSize="sm" m={0}>Inactive Agents</Text>
                      </HStack>
                    </VStack>
                  </RadioGroup>
                </PopoverBody>
              </PopoverContent>
            </Popover>
          </HStack>

          {/* Agent List */}
          <Box flex="1" overflowY="auto" mb={10}>
            {isLoading ? (
              <Center py={8}>
                <Spinner size="md" />
                <Text ml={3}>Loading agents...</Text>
              </Center>
            ) : (
              <List spacing={2}>
                {filteredHeaders?.map((header) => (
                  <ListItem key={header.id}>
                    <Box
                      p={1}
                      bg={selectedAgents.some(h => h.id === header?.id) ? "purple.700" : "white"}
                      color={selectedAgents.some(h => h.id === header?.id) ? "white" : "gray.800"}
                      borderRadius="md"
                      cursor="pointer"
                      _hover={selectedAgents.some(h => h.id === header?.id) 
                        ? { bg: "purple.700", color: "white" } 
                        : { bg: "purple.50", color: "fg.default" }}
                      transition="all 0.2s"
                      textAlign="left"
                      textOverflow="ellipsis"
                      maxWidth="100%"
                      borderWidth="1px"
                      borderColor="gray.300"
                    >
                      <Flex justify="space-between" align="center">
                      <Box 
                          flex="1" 
                          onClick={() => handleAgentSelection(header)}
                          pr={2}
                          display="flex"
                          alignItems="center"
                          height="100%"
                        >
                         
                          <Text fontSize="sm" noOfLines={1} mb={0} textAlign="left" ml={1} fontWeight="600">
                            {header.name || "Unnamed Agent"}
                          </Text>
                        </Box>
                        
                        {/* Active indicator */}
                        {header?.has_active_schedule && (
                          <Tooltip label="Active" placement="top">
                            <Box
                              w="12px"
                              h="12px"
                              borderRadius="full"
                              bg="red.500"
                              display="inline-block"
                              border="1.5px solid"
                              borderColor="bg.surface"
                              mr={2}
                            />
                          </Tooltip>
                        )}
                        
                        {/* Menu Button */}
                        <Menu>
                          <MenuButton
                            as={IconButton}
                            icon={<ChevronDownIcon />}
                            variant="ghost"
                            size="sm"
                            color={selectedAgents.some(h => h.id === header?.id) ? "white" : "black"}
                            _hover={{
                              bg: selectedAgents.some(h => h.id === header?.id) ? "purple.600" : "gray.100"
                            }}
                            onClick={(e) => e.stopPropagation()}
                          />
                          <MenuList>
                            <MenuItem 
                              icon={<CopyIcon boxSize={4}/>} 
                              onClick={() => handleDuplicateClick(header)}
                              isDisabled={duplicatingAgent?.id === header.id}
                              textColor="fg.default"
                              fontWeight="500"
                              fontSize="sm"
                            >
                              {duplicatingAgent?.id === header.id ? "Duplicating..." : "Duplicate"}
                            </MenuItem>
                            <MenuItem 
                              icon={<EditIcon boxSize={4}/>} 
                              onClick={() => handleRenameClick(header)}
                              textColor="fg.default"
                              fontWeight="500"
                              fontSize="sm"
                            >
                              Rename
                            </MenuItem>
                            <MenuItem 
                              icon={<DeleteIcon boxSize={4}/>} 
                              onClick={() => handleDeleteClick(header)}
                              color="red.500"
                              fontWeight="500"
                              fontSize="sm"
                            >
                              Delete
                            </MenuItem>
                          </MenuList>
                        </Menu>
                      </Flex>
                    </Box>
                  </ListItem>
                ))}
                {filteredHeaders?.length === 0 && !isLoading && (
                  <Center py={8}>
                    <Text color="gray.500">
                      {searchTerm
                        ? "No agents found matching your search"
                        : "No agents available"}
                    </Text>
                  </Center>
                )}
              </List>
            )}
          </Box>
        </VStack>
      </Box>

      {/* Rename Agent Modal */}
      <Modal isOpen={isRenameOpen} onClose={onRenameClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Rename Agent</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Input
              value={newAgentName}
              onChange={(e) => setNewAgentName(e.target.value)}
              placeholder="Enter new agent name"
            />
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onRenameClose}>
              Cancel
            </Button>
            <Button 
              colorScheme="blue" 
              onClick={handleConfirmRename}
              isDisabled={!newAgentName.trim()}
            >
              Rename
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Agent Confirmation */}
      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Agent
            </AlertDialogHeader>
            <AlertDialogBody>
              Are you sure you want to delete the agent "{deletingAgent?.name}"? 
              This action cannot be undone.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteClose}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={handleConfirmDelete} ml={3}>
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

    </>
  );
};

export default AgentListComponent; 